import React, { useEffect, useRef, useState } from "react";

export default function HeroCarousel({
  slides = [],            // [{src, alt, caption}]
  interval = 5000,        // autoplay ms
  height = 420,           // hauteur en px (desktop)
}) {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);

  const goTo = (i) => setActive((i + slides.length) % slides.length);
  const next = () => goTo(active + 1);
  const prev = () => goTo(active - 1);

  // autoplay
  useEffect(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, interval);
    return () => clearInterval(timerRef.current);
  }, [active, interval]);

  // clavier (← →)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  // swipe mobile
  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
  };

  if (!slides.length) return null;

  return (
    <div
      className="hero-carousel"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
    >
      <style>{`
        .hero-carousel {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-bottom: 4px solid #0ea5e9;
          background: #0b1320;
          isolation: isolate;
        }
        .hero-track {
          display: flex;
          transition: transform 600ms ease;
          will-change: transform;
          height: ${height}px;
        }
        @media (max-width: 768px){
          .hero-track { height: ${Math.round(height * 0.6)}px; }
        }
        .hero-slide {
          min-width: 100%;
          position: relative;
        }
        .hero-slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        /* Dégradé overlay bas */
        .hero-slide::after{
          content:"";
          position:absolute; inset:0;
          background: linear-gradient(to top, rgba(8,21,36,.55), transparent 55%);
          pointer-events:none;
        }
        /* Dots */
        .hero-dots {
          position: absolute;
          right: 18px;
          bottom: 14px;
          display: flex; gap: 10px;
          z-index: 2;
        }
        .hero-dot {
          width: 12px; height: 12px;
          border-radius: 9999px;
          border: 2px solid #fff;
          background: transparent;
          opacity: .9;
        }
        .hero-dot.active { background: #fff; }
        .hero-dot:hover { opacity: 1; cursor: pointer; }
        /* Flèches */
        .hero-arrow {
          position:absolute; top:50%; transform:translateY(-50%);
          width:42px; height:42px; border-radius:9999px;
          background: rgba(255,255,255,.15);
          border:none; backdrop-filter: blur(4px);
        }
        .hero-arrow:hover { background: rgba(255,255,255,.24); cursor:pointer;}
        .hero-arrow.left { left: 12px; }
        .hero-arrow.right { right: 12px; }
        .hero-arrow i { color:#fff; font-size:18px; }
        /* Légende (optionnelle) */
        .hero-caption {
          position:absolute; left:24px; bottom:22px; z-index:2;
          color:#fff;
          text-shadow: 0 2px 8px rgba(0,0,0,.6);
          font-weight: 600;
        }
      `}</style>

      <div
        className="hero-track"
        style={{ transform: `translateX(-${active * 100}%)` }}
      >
        {slides.map((s, i) => (
          <div className="hero-slide" key={i} aria-hidden={active !== i}>
            <img src={s.src} alt={s.alt || `slide ${i + 1}`} />
            {s.caption && <div className="hero-caption">{s.caption}</div>}
          </div>
        ))}
      </div>

      {/* Flèches */}
      <button className="hero-arrow left" onClick={prev} aria-label="Précédent">
        <i className="bi bi-chevron-left" />
      </button>
      <button className="hero-arrow right" onClick={next} aria-label="Suivant">
        <i className="bi bi-chevron-right" />
      </button>

      {/* Dots */}
      <div className="hero-dots" role="tablist" aria-label="Carousel Pagination">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`hero-dot ${i === active ? "active" : ""}`}
            onClick={() => goTo(i)}
            role="tab"
            aria-selected={i === active}
            aria-controls={`slide-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
